import React, { useMemo } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import css from './MegaMenu.module.css';
import List from './List/List';
import ListWithDescription from './ListWithDescription/ListWithDescription';
import ListWithImages from './ListWithImages/ListWithImages';
import { useLatestPosts } from '../../hooks/useLatestPosts';

const LIST = 'list';
const LIST_WITH_DESCRIPTION = 'listWithDescription';
const LIST_WITH_IMAGES = 'listWithImages';

/** Menu whose cards are populated from the live blog feed. */
const INSIGHTS_ID = 'insights';

/** How many post cards the panel shows. */
const POST_CARDS = 2;

/**
 * Replaces the placeholder Insights entries with the newest posts from the
 * blog.
 *
 * The static config stays the single source of truth for layout, the aside
 * card and the "Explore topics" links; only the post cards are dynamic. If
 * the feed is empty or unreachable the original config is returned untouched,
 * so the menu always renders something useful.
 *
 * @param {object} config  Nav entry from configNav
 * @param {Array}  posts   Normalised posts from the API
 */
const withLivePosts = (config, posts) => {
  if (!posts.length) return config;

  const staticGroups = config.config.navConfig.filter(item => Array.isArray(item.config));

  /*
   * Prefer posts that have a cover image: the card layout is image-led, and
   * a WordPress post without a featured image is common. Falls back to the
   * newest posts regardless if too few have one.
   */
  const withCover = posts.filter(post => post.image);
  const source = withCover.length >= POST_CARDS ? withCover : posts;

  const postCards = source.slice(0, POST_CARDS).map(post => ({
    id: `post-${post.id}`,
    label: post.category || 'Latest',
    hideTitle: !post.category,
    description: post.title,
    image: post.image,
    link: post.url,
  }));

  return {
    ...config,
    config: { ...config.config, navConfig: [...postCards, ...staticGroups] },
  };
};

const MegaMenu = ({ id, rootClassName, className, config, onLinkClick }) => {
  const classes = classNames(rootClassName || css.root, className);

  // Hooks must run unconditionally, so this sits above the early return.
  const { posts } = useLatestPosts();

  const resolvedConfig = useMemo(() => {
    if (!config) return null;
    return config.id === INSIGHTS_ID ? withLivePosts(config, posts) : config;
  }, [config, posts]);

  if (!resolvedConfig) return null;

  const renderMenu = () => {
    switch (resolvedConfig.config.layout) {
      case LIST:
        return <List config={resolvedConfig} onLinkClick={onLinkClick} />;
      case LIST_WITH_DESCRIPTION:
        return <ListWithDescription config={resolvedConfig} onLinkClick={onLinkClick} />;
      case LIST_WITH_IMAGES:
        return <ListWithImages config={resolvedConfig} onLinkClick={onLinkClick} />;
      default:
        return null;
    }
  };

  return (
    <div className={classes} id={id}>
      {/* Panel offset is owned by the topbar, which knows the pill's height. */}
      <div className={css.megaMenu}>{renderMenu()}</div>
    </div>
  );
};

MegaMenu.propTypes = {
  id: PropTypes.string,
  rootClassName: PropTypes.string,
  className: PropTypes.string,
  config: PropTypes.object,
  onLinkClick: PropTypes.func,
};

export default MegaMenu;
